import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { fetchRoleList } from 'actions/Roles';
import { fetchSiteUserList } from 'actions/Users';
import { auctionUnit, reassignAuction } from 'actions/Units';
import { submitNotificationPerformance, fetchSiteSettings } from 'actions/App';
import { toastManager } from "components/ToastNotification";
import { validateEmail, getDateFormat } from "utilities"
import moment from 'moment';
import ButtonContained from "components/ButtonContained";
import CountryCodeSelector from "components/CountryCodeSelector";
import TextInput from "components/TextInput";
import SelectInput from "components/SelectInput";
import DateInput from "components/DateInput";
import NumberInput from "components/NumberInput";

const AuctionUnit = ({ onClose, facility, selectedUnit, mode, refetch = null }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.roles.rolesList);
  const siteUserList = useSelector((state) => state.users.siteUserList);
  const country = useSelector((state) => state.app.country);
  const authUser = useSelector((state) => state.auth.authUser);

  const [winner, setWinner] = useState(true);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: country,
    phone: "",
    role: "",
    employee: "",
    expirationType: "date",
    expirationDate: null,
    expirationUnlock: 1
  });
  useEffect(() => {
    dispatch(fetchSiteSettings({sites: [facility.uuid]}));
    dispatch(fetchRoleList(
      undefined,
      {
        facilityUUID: facility.uuid,
        companyUUID: facility.companyUUID
      }
    ));
    dispatch(fetchSiteUserList(
      undefined,
      undefined,
      true,
      undefined,
      undefined,
      undefined,
      {
        facilityUUID: facility.uuid,
        companyUUID: facility.companyUUID
      }
    ));
  }, [dispatch]);

  const handleSubmit = () => {
    const {
      firstName,
      lastName,
      email,
      phone,
      country,
      role,
      employee,
      expirationType,
      expirationDate,
      expirationUnlock
    } = formData;
    if (winner && firstName === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.firstName"}));
      return;
    } else if (winner && lastName === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.lastName"}));
      return;
    } else if (winner && email === "" && phone === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.phoneEmail"}));
      return;
    } else if (winner && email !== "" && !validateEmail(email)) {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.fail.email"}));
      return;
    } else if (winner && phone !== "" && phone.length < 4) {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.phoneEmail"}));
      return;
    } else if (winner && role === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.UserRoleId"}));
      return;
    } else if (expirationType === "date" && !expirationDate) {
      getDateFormat(authUser, facility) == "DD-MM-YYYY"
      ?
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.invalidDateReversed"}))
      :
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.notifications.error.blank.invalidDate"}))
      return;
    } else {
      submitNotificationPerformance({
        site_id: facility.id,
        notification_id: phone && phone !== '' ? phone : email,
        notification_type: 'unitNotifications',
        notification_name: 'new_account',
        start_time: moment().utc()
      });
      const auctionData = {
        firstName: winner ? firstName : employee.firstName,
        lastName: winner ? lastName : employee.lastName,
        email: winner ? email : employee.email,
        phone: winner ? phone : employee.phone,
        countryCode: winner ? country.phone[0].toString() : employee.countryCode,
        roleUUID: winner ? role.uuid : employee.role.uuid,
        expirationType: expirationType,
        expirationDate: expirationType === "unlock" ? null : expirationDate.format('YYYY-MM-DD'),
        expirationUnlock: parseInt(expirationUnlock),
        unitUUID: selectedUnit.id.toString(),
        userUUID: winner ? "" : employee.uuid
      }
      const overwrite = {
        facilityUUID: facility.uuid,
        companyUUID: facility.companyUUID,
        message: intl.formatMessage({id: "notifications.success.assign"}),
        refetch: refetch
      }
      if (mode === "auction") {
        dispatch(auctionUnit(auctionData, overwrite));
      } else {
        dispatch(reassignAuction(auctionData, overwrite));
      }
      onClose && onClose();
    }
  }

  const roleOptions = roles && roles.filter(role => role.tier === 0);
  const roleMap = roleOptions && Object.fromEntries(roleOptions.map(role => [role.uuid, role]));
  const userOptions = siteUserList && siteUserList.filter(user => user.role.tier > 1);
  const userMap = userOptions && Object.fromEntries(userOptions.map(user => [user.uuid, user]));
  return (
    <div className="flex flex-col px-10 space-y-8">
      <div className="flex justify-center gap-2">
        <ButtonContained
          id="Auction-Unit-Modal-Winner-Button"
          dataCy="Auction-Unit-Modal-Winner-Button"
          text={intl.formatMessage({ id: 'form.auctionWinner' })}
          type={winner ? "default" : "inactive"}
          onClick={() => setWinner(true)}
        />
        <ButtonContained
          id="Auction-Unit-Modal-Employee-Button"
          dataCy="Auction-Unit-Modal-Employee-Button"
          text={intl.formatMessage({ id: 'form.tempEmployee' })}
          type={winner ? "inactive" : "default"}
          onClick={() => setWinner(false)}
        />
      </div>
      {winner && <div className="flex-1 flex flex-col gap-4">
        <TextInput
          id="Auction-Unit-Modal-First-Name"
          dataCy="Auction-Unit-Modal-First-Name"
          placeholder={`${intl.formatMessage({id: "form.firstName"})} (${intl.formatMessage({id: "form.required"})})`}
          onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
          value={formData.firstName}
        />
        <TextInput
          id="Auction-Unit-Modal-Last-Name"
          dataCy="Auction-Unit-Modal-Last-Name"
          placeholder={`${intl.formatMessage({id: "form.lastName"})} (${intl.formatMessage({id: "form.required"})})`}
          onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
          value={formData.lastName}
        />
        <TextInput
          id="Auction-Unit-Modal-Email"
          dataCy="Auction-Unit-Modal-Email"
          placeholder={`${intl.formatMessage({id: "form.email"})}`}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          value={formData.email}
        />
        <div className="flex items-center gap-2 h-[53px]">
          <CountryCodeSelector
            selectedCountry={formData.country}
            onSelect={(country) => setFormData(prev => ({ ...prev, country }))}
          />
          <TextInput
            id="Auction-Unit-Modal-Phone"
            dataCy="Auction-Unit-Modal-Phone"
            placeholder={`${intl.formatMessage({id: "form.phone"})}`}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            value={formData.phone}
            validateInput={(val) => /^\d*$/.test(val)} // only digits
          />
        </div>
        <SelectInput
          id="Auction-Unit-Modal-Role"
          dataCy="Auction-Unit-Modal-Role"
          value={formData.role?.uuid || ""}
          placeholder={`${intl.formatMessage({id: "form.role"})} (${intl.formatMessage({id: "form.required"})})`}
          onChange={(e) => {
            const selectedUuid = e.target.value;
            const selectedRole = roleMap[selectedUuid];
            setFormData(prev => ({ ...prev, role: selectedRole }))
          }}
          options={
            roleOptions
            ? roleOptions.map(role => ({
              value: role.uuid,
              name: role.name
            }))
            : []
          }
        />
      </div>}
      {!winner && <div className="flex flex-col">
        <SelectInput
          id="Auction-Unit-Modal-Employee"
          dataCy="Auction-Unit-Modal-Employee"
          value={formData.employee?.uuid || ""}
          placeholder={intl.formatMessage({ id: 'role.employee' })}
          onChange={(e) => {
            const selectedUuid = e.target.value;
            const selectedUser = userMap[selectedUuid];
            setFormData(prev => ({ ...prev, employee: selectedUser }))
          }}
          options={
            userOptions
            ? userOptions.map(user => ({
              value: user.uuid,
              name: user.name
            }))
            : []
          }
        />
      </div>}
      <div className="flex flex-col">
        <SelectInput
          id="Auction-Unit-Modal-Expiration-Type"
          dataCy="Auction-Unit-Modal-Expiration-Type"
          value={formData.expirationType || ""}
          placeholder={`${intl.formatMessage({ id: 'form.expirationType' })} (${intl.formatMessage({id: "form.required"})})`}
          onChange={(e) => {setFormData(prev => ({ ...prev, expirationType: e.target.value }))}}
          options={[
            {
              name: intl.formatMessage({ id: 'form.date' }),
              value: "date"
            },
            {
              name: intl.formatMessage({ id: 'form.unlockCount' }),
              value: "unlock"
            }
          ]}
        />
      </div>
      {formData.expirationType === "date" && <DateInput
        id="Auction-Unit-Modal-Expiration-Date"
        dataCy="Auction-Unit-Modal-Expiration-Date"
        placeholder={`${intl.formatMessage({ id: 'form.date' })} - ${intl.formatMessage({ id: 'db.auction.date' })}`}
        date={formData.expirationDate}
        onDateChange={(date) => setFormData(prev => ({ ...prev, expirationDate: date }))}
        displayFormat={facility.sitePreferredDateFormat ? facility.sitePreferredDateFormat : "MM/DD/YYYY"}
      />}
      {formData.expirationType === "unlock" && <NumberInput
        id="Auction-Unit-Modal-Expiration-Unlock-Count"
        dataCy="Auction-Unit-Modal-Expiration-Unlock-Count"
        placeholder={intl.formatMessage({ id: 'form.unlockCount' })}
        min={1}
        onChange={(e) => setFormData(prev => ({ ...prev, expirationUnlock: e.target.value }))}
        value={formData.expirationUnlock}
      />}
      <div className="flex justify-end">
        <ButtonContained
          id="Auction-Unit-Modal-Submit-Button"
          dataCy="Auction-Unit-Modal-Submit-Button"
          text={intl.formatMessage({id: "comp.button.submit"})}
          type="default"
          onClick={handleSubmit}
        />
      </div>
    </div>
  )
}

export default AuctionUnit;